/**
 * Ancestor utility functions for computing pedigree and lineage data.
 * Used by the AncestorPedigreeView component to render ancestor charts.
 */

const MAX_GENERATIONS_CAP = 50;
const DEFAULT_MAX_GENERATIONS = 4;

/**
 * Compute ancestor pedigree tree for a person.
 * Returns a recursive tree structure with father/mother branches.
 *
 * @param {Object} gedcomData - Parsed GEDCOM data { individuals, families }
 * @param {string} personId - Starting person ID
 * @param {number} [maxGenerations=4] - Maximum depth (capped at 50)
 * @returns {Object|null} Pedigree tree: { person, generation, position, father, mother }
 */
export const getAncestorPedigree = (gedcomData, personId, maxGenerations = DEFAULT_MAX_GENERATIONS) => {
  if (!gedcomData || !gedcomData.individuals || !gedcomData.families) {
    return null;
  }

  const person = gedcomData.individuals[personId];
  if (!person) {
    return null;
  }

  const effectiveMax = Math.min(Math.max(1, maxGenerations), MAX_GENERATIONS_CAP);

  const buildTree = (currentId, generation, position, visited) => {
    if (!currentId || visited.has(currentId)) {
      return null;
    }

    const individual = gedcomData.individuals[currentId];
    if (!individual) {
      return null;
    }

    visited.add(currentId);

    const node = {
      person: individual,
      generation,
      position,
      father: null,
      mother: null
    };

    // Stop recursion if we've reached the max generation depth
    if (generation >= effectiveMax) {
      visited.delete(currentId);
      return node;
    }

    // Find parents via the family record
    const parentFamilyId = individual.parents;
    if (parentFamilyId) {
      const parentFamily = gedcomData.families[parentFamilyId];
      if (parentFamily) {
        if (parentFamily.husband) {
          node.father = buildTree(parentFamily.husband, generation + 1, 'paternal', visited);
        }
        if (parentFamily.wife) {
          node.mother = buildTree(parentFamily.wife, generation + 1, 'maternal', visited);
        }
      }
    }

    visited.delete(currentId);
    return node;
  };

  const visited = new Set();
  return buildTree(personId, 0, 'self', visited);
};

/**
 * Compute flat ancestor list with generation and position info.
 * Returns ancestors only (starting person at generation 0 is NOT included).
 * Results are ordered by generation (breadth-first).
 *
 * @param {Object} gedcomData - Parsed GEDCOM data { individuals, families }
 * @param {string} personId - Starting person ID
 * @param {number} [maxGenerations=4] - Maximum depth (capped at 50)
 * @returns {Array} Array of { person, generation, position }
 */
export const getAncestorList = (gedcomData, personId, maxGenerations = DEFAULT_MAX_GENERATIONS) => {
  if (!gedcomData || !gedcomData.individuals || !gedcomData.families) {
    return [];
  }

  const person = gedcomData.individuals[personId];
  if (!person) {
    return [];
  }

  const effectiveMax = Math.min(Math.max(1, maxGenerations), MAX_GENERATIONS_CAP);
  const ancestors = [];
  const visited = new Set();
  visited.add(personId);

  // BFS queue: each entry is { id, generation, position }
  const queue = [];

  // Seed the queue with the starting person's parents
  const parentFamilyId = person.parents;
  if (parentFamilyId) {
    const parentFamily = gedcomData.families[parentFamilyId];
    if (parentFamily) {
      if (parentFamily.husband) {
        queue.push({ id: parentFamily.husband, generation: 1, position: 'paternal' });
      }
      if (parentFamily.wife) {
        queue.push({ id: parentFamily.wife, generation: 1, position: 'maternal' });
      }
    }
  }

  while (queue.length > 0) {
    const { id, generation, position } = queue.shift();

    // Skip if generation exceeds cap
    if (generation > effectiveMax) {
      continue;
    }

    // Circular reference detection: if this person ID is already in visited,
    // terminate traversal for this branch (do NOT include the duplicate entry)
    if (visited.has(id)) {
      continue;
    }

    const individual = gedcomData.individuals[id];
    if (!individual) {
      continue;
    }

    // Include this ancestor in the results BEFORE checking for cycles further up
    visited.add(id);
    ancestors.push({
      person: individual,
      generation,
      position
    });

    // Enqueue this ancestor's parents for further traversal
    // If the parent's ID is already in visited, the cycle will be detected
    // at the top of the next iteration and that branch will terminate
    if (generation < effectiveMax) {
      const ancestorParentFamilyId = individual.parents;
      if (ancestorParentFamilyId) {
        const ancestorParentFamily = gedcomData.families[ancestorParentFamilyId];
        if (ancestorParentFamily) {
          if (ancestorParentFamily.husband) {
            queue.push({ id: ancestorParentFamily.husband, generation: generation + 1, position: 'paternal' });
          }
          if (ancestorParentFamily.wife) {
            queue.push({ id: ancestorParentFamily.wife, generation: generation + 1, position: 'maternal' });
          }
        }
      }
    }
  }

  return ancestors;
};
